import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';

const ThemeContext = createContext();

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export const ThemeProvider = ({ children }) => {
  const [isDark, setIsDark] = useState(() => {
    // Check localStorage first; default to light when no saved preference
    const savedTheme = localStorage.getItem('heatos-theme');
    if (savedTheme) {
      return savedTheme === 'dark';
    }
    return false;
  });

  useEffect(() => {
    // Save theme preference
    localStorage.setItem('heatos-theme', isDark ? 'dark' : 'light');
    
    // Apply theme to document
    document.documentElement.setAttribute('data-theme', isDark ? 'dark' : 'light');
    
    // Update CSS custom properties
    const root = document.documentElement;
    
    if (isDark) {
      // Dark theme colors — lifted base + cool undertone for less oppressive feel,
      // wider gap between page bg and card so cards visibly float.
      root.style.setProperty('--bg-primary', '#131418');
      root.style.setProperty('--bg-secondary', '#1a1b20');
      root.style.setProperty('--bg-tertiary', '#25272e');
      root.style.setProperty('--bg-card', '#21232a');
      root.style.setProperty('--bg-hover', '#2b2d35');

      root.style.setProperty('--text-primary', '#ebedf0');
      root.style.setProperty('--text-secondary', '#a3a8b2');
      root.style.setProperty('--text-muted', '#6b6f78');
      
      root.style.setProperty('--border-subtle', '#333333');
      root.style.setProperty('--border-medium', '#404040');
      
      root.style.setProperty('--accent-gradient', 'var(--accent-blue)');
      root.style.setProperty('--accent-opposite', 'var(--accent-blue)');
    } else {
      // Light theme colors
      root.style.setProperty('--bg-primary', '#ffffff');
      root.style.setProperty('--bg-secondary', '#f8f9fa');
      root.style.setProperty('--bg-tertiary', '#e9ecef');
      root.style.setProperty('--bg-card', '#ffffff');
      root.style.setProperty('--bg-hover', '#f5f5f5');
      
      root.style.setProperty('--text-primary', '#1a1a1a');
      root.style.setProperty('--text-secondary', '#4a5568');
      root.style.setProperty('--text-muted', '#9ca3af');
      
      root.style.setProperty('--border-subtle', '#e5e7eb');
      root.style.setProperty('--border-medium', '#d1d5db');
      
      root.style.setProperty('--accent-gradient', 'var(--accent-blue)');
      root.style.setProperty('--accent-opposite', 'var(--accent-blue)');
    }
  }, [isDark]);

  const toggleTheme = useCallback(() => {
    setIsDark(prev => !prev);
  }, []);

  const value = useMemo(() => ({
    isDark,
    toggleTheme,
    theme: isDark ? 'dark' : 'light'
  }), [isDark, toggleTheme]);

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};

export default ThemeContext; 